import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { resolveImage } from "@/lib/api";
import { Footer } from "@/components/site/Footer";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts")
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-bone min-h-screen" data-testid="blog-page">
      <div className="noise-overlay" />
      <header className="border-b border-forest/15 bg-bone/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="blog-back-link" className="group flex items-center gap-2 text-sm font-bold text-forest hover:text-terra transition-colors">
            <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
            Back to home
          </Link>
          <span className="font-serif italic font-bold text-forest text-lg">The Journal</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-14">
          <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra">News & Stories</span>
          <h1 className="font-serif font-bold tracking-tighter text-forest text-5xl lg:text-7xl mt-4" data-testid="blog-heading">
            Notes from the <span className="italic text-terra">field.</span>
          </h1>
        </motion.div>

        {loading ? (
          <p className="font-serif italic text-moss text-xl" data-testid="blog-loading">Unfolding the journal…</p>
        ) : posts.length === 0 ? (
          <p className="text-moss" data-testid="blog-empty">No stories yet — check back soon.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group border border-forest/20 bg-bone hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300"
                data-testid={`blog-card-${p.slug}`}
              >
                <Link to={`/blog/${p.slug}`} className="block">
                  {p.cover_image && (
                    <div className="overflow-hidden border-b border-forest/20">
                      <img src={resolveImage(p.cover_image)} alt={p.title} className="w-full h-56 object-cover img-warm transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-terra">
                      {new Date(p.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </span>
                    <h2 className="font-serif font-bold text-2xl text-forest tracking-tight mt-2 leading-snug">{p.title}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-moss line-clamp-3">{p.excerpt}</p>
                    <span className="inline-block mt-4 text-xs font-bold tracking-[0.2em] uppercase text-forest group-hover:text-terra transition-colors">Read story</span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
